import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AuthStore, { type WorkspaceStatus } from './flux/stores/AuthStore';
import Actions from './flux/Actions';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import LoadingScreen from './components/LoadingScreen';
import { ToastProvider } from './components/Toast';

function App() {
   const [isAuthenticated, setIsAuthenticated] = useState(false);
   const [isLoading, setIsLoading] = useState(true);
   const [workspaceStatus, setWorkspaceStatus] = useState<WorkspaceStatus>('SIGNED_OUT');
   const [couchError, setCouchError] = useState<string | null>(null);
   const [isOnline, setIsOnline] = useState(
      typeof navigator === 'undefined' ? true : navigator.onLine,
   );

   useEffect(() => {
      Actions.initAuthListener();

      const handleAuthChange = () => {
         setIsAuthenticated(AuthStore.getIsAuthenticated());
         setIsLoading(AuthStore.getIsLoading());
         setWorkspaceStatus(AuthStore.getWorkspaceStatus());
         setCouchError(
            AuthStore.getCouchStatus() === 'error' ? AuthStore.getCouchMessage() : null,
         );

         if (AuthStore.needsCredentialsRefresh()) {
            AuthStore.clearCredentialsRefreshFlag();
            Actions.refreshCouchCredentials();
         }
      };

      AuthStore.addChangeListener(handleAuthChange);
      handleAuthChange();

      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
         AuthStore.removeChangeListener(handleAuthChange);
         window.removeEventListener('online', handleOnline);
         window.removeEventListener('offline', handleOffline);
      };
   }, []);

   // FIX 3: Show loading screen while workspace is not yet ready after login
   if (isLoading) {
      return <LoadingScreen message="Starting up..." />;
   }

   if (isAuthenticated && workspaceStatus !== 'READY') {
      const isError = AuthStore.getCouchStatus() === 'error';
      return (
         <LoadingScreen
            message={
               workspaceStatus === 'PROVISIONING'
                  ? 'Preparing your workspace...'
                  : 'Connecting to database...'
            }
            error={isError ? (couchError ?? 'Failed to connect to database.') : null}
            onRetry={isError ? () => Actions.refreshCouchCredentials() : undefined}
         />
      );
   }

   return (
      <ToastProvider>
         <div className="h-screen overflow-hidden bg-ink noise-overlay relative">
            <AnimatePresence mode="wait">
               {isAuthenticated ? (
                  <motion.div
                     key="dashboard"
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     exit={{ opacity: 0 }}
                     transition={{ duration: 0.3 }}
                     className="h-full"
                  >
                     <Dashboard />
                  </motion.div>
               ) : (
                  <motion.div
                     key="auth"
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     exit={{ opacity: 0 }}
                     transition={{ duration: 0.3 }}
                     className="h-full"
                  >
                     <Auth />
                  </motion.div>
               )}
            </AnimatePresence>
         </div>
      </ToastProvider>
   );
}

export default App;
